// src/context/AuthContext.jsx - Relationship Compass Auth Context
//
// The signed-in person is their DDD account in the Auth Hub (authHubAuth),
// the same as MindEase. Cloud backup goes to that account (services/hubSync.js),
// so the hub's account deletion reaches it. Sign-in happens on the hub; this
// context finishes the hand-back and mirrors the account here.

import React, { createContext, useContext, useState, useEffect } from 'react';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { authHubAuth } from '../services/firebase';
import { completeHubSignIn, hasPendingHubSignIn, redirectToHub, redirectToHubLogout } from '../services/authHub';

const AuthContext = createContext({});

// eslint-disable-next-line react-refresh/only-export-components -- hook shares the provider's context
export const useAuth = () => useContext(AuthContext);

const IDENTITY_KEYS = [
  'relationshipCompass_isSignedIn',
  'relationshipCompass_userId',
  'relationshipCompass_userEmail',
  'relationshipCompass_displayName',
  'relationshipCompass_authType',
];

const mirrorIdentity = (userData) => {
  try {
    if (userData) {
      localStorage.setItem('relationshipCompass_isSignedIn', 'true');
      localStorage.setItem('relationshipCompass_userId', userData.uid);
      localStorage.setItem('relationshipCompass_userEmail', userData.email || '');
      localStorage.setItem('relationshipCompass_displayName', userData.displayName);
      localStorage.setItem('relationshipCompass_authType', userData.authType);
    } else {
      IDENTITY_KEYS.forEach((key) => localStorage.removeItem(key));
    }
  } catch {
    /* storage blocked: the in-memory state still works */
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // Held while a sign-in handed back by the Auth Hub completes, so the page
  // doesn't flash signed-out first.
  const [completingSignIn, setCompletingSignIn] = useState(hasPendingHubSignIn);

  useEffect(() => {
    if (!authHubAuth) {
      // Built without the hub (no VITE_AUTH_HUB_API_KEY): signed out, and
      // everything stays on this device.
      mirrorIdentity(null);
      setLoading(false);
      setCompletingSignIn(false);
      return undefined;
    }

    const unsubscribe = onAuthStateChanged(authHubAuth, (hubUser) => {
      if (hubUser) {
        const userData = {
          uid: hubUser.uid,
          email: hubUser.email,
          displayName: hubUser.displayName || hubUser.email?.split('@')[0] || 'User',
          authType: hubUser.isAnonymous ? 'anonymous' : 'authenticated',
          isAnonymous: hubUser.isAnonymous
        };
        setUser(userData);
        mirrorIdentity(userData);
      } else {
        setUser(null);
        mirrorIdentity(null);
      }
      setLoading(false);
    });

    if (hasPendingHubSignIn()) {
      // Returning from the Auth Hub: main.jsx has already lifted the tokens
      // out of the URL. onAuthStateChanged above picks up the hub user.
      completeHubSignIn()
        .catch((error) => {
          console.error('[AuthContext] Auth Hub sign-in could not be completed:', error?.code || error?.message || error);
        })
        .finally(() => setCompletingSignIn(false));
    }

    return () => unsubscribe();
  }, []);

  // Sign in (or sign up) through the central DDD Auth Hub.
  const redirectToAuth = (action = 'signin') => redirectToHub(action);

  // Sign out here, then end the hub's own session too (the hub keeps one on
  // its own domain). Without the second step the next Log In landed on the
  // hub's "You're already signed in" page. The hub sends the browser back to
  // this app's home page. Resolves to true when the browser is on its way to
  // the hub, so callers don't navigate (that could cancel the redirect).
  const logOut = async () => {
    try {
      if (authHubAuth) await signOut(authHubAuth);
    } catch (error) {
      console.error('Error signing out:', error);
    }
    mirrorIdentity(null);
    setUser(null);
    if (!authHubAuth) return false;
    redirectToHubLogout();
    return true;
  };

  const value = {
    user,
    loading,
    isAuthenticated: !!user && !user.isAnonymous,

    // Auth actions
    redirectToAuth,
    logOut,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && !completingSignIn && children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
